import { useEffect, useState } from "react";
import {
  AD_RUN_DAYS,
  KIJIJI_POST_URL,
  type AdPurpose,
  draftRecruitingAd,
  draftServicesAd,
  kijijiRegions,
} from "@hunts/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { businessContact } from "../config";
import { ErrorNote, Loading, StatusPill, formatDate } from "./Page";

interface Ad {
  id: string;
  purpose?: AdPurpose;
  region: string;
  title: string;
  body: string;
  status: "DRAFT" | "POSTED" | "EXPIRED";
  postedAt?: string;
  expiresAt?: string;
  url?: string;
  createdAt: string;
}

/**
 * Drafts a recruiting ad and keeps track of the ones that are live.
 *
 * Posting is a manual step on purpose: Kijiji's terms prohibit automated
 * posting and the form is behind a login and bot detection, so automating it
 * would risk the account. This does everything except the click -- writes the
 * copy, puts it on the clipboard, opens the form, and remembers what is running
 * where.
 */
export function AdsTab({ purpose }: { purpose: AdPurpose }) {
  const recruiting = purpose === "recruiting";
  const draft = recruiting ? draftRecruitingAd : draftServicesAd;
  const qc = useQueryClient();
  const [region, setRegion] = useState(kijijiRegions[0]!.slug);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [edited, setEdited] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  /**
   * The saved ad being worked on, if any.
   *
   * Without this, saving twice made two records. Wording an ad is iterative --
   * you write it, read it back, change a line -- and every pass leaving another
   * near-identical row behind is how the list becomes useless.
   */
  const [editingId, setEditingId] = useState<string | null>(null);

  // Regenerate when the region changes, unless the copy has been hand-edited or
  // belongs to a saved ad -- silently discarding someone's rewrite would be
  // worse than a stale draft.
  useEffect(() => {
    if (edited || editingId) return;
    const r = kijijiRegions.find((x) => x.slug === region);
    if (!r) return;
    const d = draft(r, businessContact);
    setTitle(d.title);
    setBody(d.body);
  }, [region, edited, editingId, draft]);

  const all = useQuery({ queryKey: ["ads"], queryFn: () => api.list<Ad>("ads") });
  const ads = {
    ...all,
    // Rows written before ads had a purpose were all recruiting ads.
    data: (all.data ?? []).filter((a) => (a.purpose ?? "recruiting") === purpose),
  };

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["ads"] });

  /**
   * Writes the copy, as a draft or as posted.
   *
   * Updates the record already open rather than adding another, so a save is
   * idempotent from the writer's point of view: press it twice and there is
   * still one ad.
   */
  const save = useMutation({
    mutationFn: async ({ status }: { status: "DRAFT" | "POSTED" }) => {
      const fields: Record<string, unknown> = { purpose, region, title, body, status };

      if (status === "POSTED") {
        const now = new Date();
        fields["postedAt"] = now.toISOString();
        fields["expiresAt"] = new Date(
          now.getTime() + AD_RUN_DAYS * 86_400_000,
        ).toISOString();
      }

      return editingId
        ? api.update<Ad>("ads", editingId, fields)
        : api.create<Ad>("ads", fields);
    },
    onSuccess: (ad) => {
      invalidate();
      // Stay on the record just written, so the next save edits it.
      setEditingId(ad.id);
      setEdited(false);
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Ad["status"] }) =>
      api.update<Ad>("ads", id, { status }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove("ads", id),
    onSuccess: invalidate,
  });

  const copy = async (label: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 2000);
  };

  const regenerate = () => {
    const r = kijijiRegions.find((x) => x.slug === region);
    if (!r) return;
    const d = draft(r, businessContact);
    setTitle(d.title);
    setBody(d.body);
    setEdited(false);
  };

  /** True when there is work in the editor that is not in the database. */
  const unsaved = edited || (!editingId && title !== "");

  const confirmDiscard = () =>
    !unsaved || window.confirm("You have unsaved changes to this ad. Discard them?");

  /** Loads a saved ad back into the editor. */
  const openAd = (ad: Ad) => {
    if (!confirmDiscard()) return;
    setEditingId(ad.id);
    setRegion(ad.region);
    setTitle(ad.title);
    setBody(ad.body);
    setEdited(false);
  };

  /** Back to a fresh generated draft. */
  const startNew = () => {
    if (!confirmDiscard()) return;
    setEditingId(null);
    setEdited(false);
    regenerate();
  };

  return (
    <>
      <h2 className="page-subhead">
        {editingId
          ? "Editing a saved ad"
          : recruiting
            ? "Write a recruiting ad"
            : "Write an ad for work"}
        {unsaved && <span className="page-subhead__note"> · unsaved changes</span>}
      </h2>

      <p className="muted" style={{ maxWidth: "62ch" }}>
        {recruiting
          ? "Drafts a recruiting ad for cleaners. "
          : "Drafts an ad offering your cleaning services. "}
        Save the wording whenever you like — a saved draft is yours to come back to.
        Kijiji doesn't allow ads to be posted automatically, so when you're ready this
        copies the text and opens the form for you to paste and submit.
      </p>

      <div className="form-card">
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Region</span>
            <select value={region} onChange={(e) => setRegion(e.target.value)}>
              {kijijiRegions.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field field--wide">
            <span className="field__label">Ad title</span>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setEdited(true);
              }}
            />
          </label>

          <label className="field field--wide">
            <span className="field__label">Ad text</span>
            <textarea
              rows={16}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setEdited(true);
              }}
            />
            <span className="field__help">
              Edit before posting — an ad that reads like a form letter is an ad nobody
              answers.
            </span>
          </label>
        </div>

        <div className="form-card__actions">
          <button
            className="btn btn--primary btn--sm"
            type="button"
            disabled={save.isPending || !title.trim() || !body.trim()}
            onClick={() => save.mutate({ status: "DRAFT" })}
          >
            {save.isPending ? "Saving…" : editingId ? "Save changes" : "Save draft"}
          </button>
          <button
            className="btn btn--sm"
            type="button"
            onClick={() => void copy("title", title)}
          >
            {copied === "title" ? "Copied ✓" : "Copy title"}
          </button>
          <button
            className="btn btn--sm"
            type="button"
            onClick={() => void copy("body", body)}
          >
            {copied === "body" ? "Copied ✓" : "Copy ad text"}
          </button>
          <a
            className="btn btn--sm"
            href={KIJIJI_POST_URL}
            target="_blank"
            rel="noreferrer noopener"
          >
            Open Kijiji post form
          </a>
          <button
            className="btn btn--sm btn--ghost"
            type="button"
            disabled={save.isPending || !title.trim()}
            onClick={() => save.mutate({ status: "POSTED" })}
          >
            I posted this
          </button>
          {(edited || editingId) && (
            <button className="btn btn--sm btn--ghost" type="button" onClick={startNew}>
              {editingId ? "New ad" : "Reset to draft"}
            </button>
          )}
        </div>
      </div>

      {save.error != null && <ErrorNote error={save.error} />}

      <h2 className="page-subhead">Saved ads</h2>
      {ads.isLoading ? (
        <Loading />
      ) : ads.error ? (
        <ErrorNote error={ads.error} />
      ) : (ads.data ?? []).length === 0 ? (
        <p className="muted">Nothing saved yet. Save a draft above and it appears here.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Region</th>
                <th>Title</th>
                <th>Status</th>
                <th>Posted</th>
                <th>Runs until</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {(ads.data ?? []).map((ad) => {
                const lapsed =
                  ad.status === "POSTED" &&
                  ad.expiresAt !== undefined &&
                  new Date(ad.expiresAt) < new Date();
                return (
                  <tr key={ad.id} className={ad.id === editingId ? "table__row--open" : undefined}>
                    <td>{kijijiRegions.find((r) => r.slug === ad.region)?.label ?? ad.region}</td>
                    <td title={ad.title}>{ad.title.slice(0, 48)}</td>
                    <td>
                      <StatusPill status={lapsed ? "EXPIRED" : ad.status} />
                    </td>
                    <td>{formatDate(ad.postedAt)}</td>
                    <td>{formatDate(ad.expiresAt)}</td>
                    <td className="table__actions">
                      <button
                        className="btn btn--sm btn--ghost"
                        onClick={() => openAd(ad)}
                        disabled={ad.id === editingId}
                      >
                        {ad.id === editingId ? "Editing" : "Edit"}
                      </button>
                      {ad.status !== "EXPIRED" && (
                        <button
                          className="btn btn--sm btn--ghost"
                          disabled={setStatus.isPending}
                          onClick={() => setStatus.mutate({ id: ad.id, status: "EXPIRED" })}
                        >
                          Mark ended
                        </button>
                      )}
                      <button
                        className="btn btn--sm btn--danger"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm("Delete this ad record?")) remove.mutate(ad.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
